// Copyright 2026 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React from "react";
import {Link, useHistory} from "react-router-dom";
import {Alert, Button, Space, Tabs, Tag, Typography} from "antd";
import {
  AppstoreAddOutlined,
  ArrowRightOutlined,
  ClockCircleOutlined,
  ShoppingCartOutlined,
  UserAddOutlined,
  UserDeleteOutlined
} from "@ant-design/icons";
import {OperationGuideType, startOperationGuide} from "../common/OperationGuideTour";

const {Paragraph, Text, Title} = Typography;

const GuideSteps = ({items}) => (
  <ol className="usage-guide-steps">
    {items.map((item, index) => (
      <li key={item.title}>
        <span className="usage-guide-step-number">{index + 1}</span>
        <div>
          <Text strong>{item.title}</Text>
          <Paragraph>{item.description}</Paragraph>
        </div>
      </li>
    ))}
  </ol>
);

const SectionTitle = ({icon, title}) => (
  <div className="usage-guide-section-title">
    {icon}
    <Title level={3}>{title}</Title>
  </div>
);

const UserGuide = ({history, canStartGuide}) => (
  <div className="usage-guide-tab">
    <section className="usage-guide-section">
      <SectionTitle icon={<UserAddOutlined />} title="添加用户" />
      <GuideSteps items={[
        {
          title: "进入用户列表",
          description: "打开“用户管理 > 用户”。如果管理多个组织，请先在页头的组织选择器中选中目标组织。",
        },
        {
          title: "创建用户",
          description: "点击用户列表上方的“添加”。使用蒙版引导时，请直接点击高亮的“添加”按钮；进入“新用户”编辑页后，第二段账号信息引导会自动开始。",
        },
        {
          title: "填写账号信息",
          description: "确认用户名、显示名称、密码、邮箱和注册应用。用户名用于唯一标识用户，创建后修改前请确认不会影响已有业务。",
        },
        {
          title: "保存并返回",
          description: "点击“保存 & 退出”回到列表。若不再创建该用户，请点击“取消”，避免保留未配置完成的账号。",
        },
      ]} />
      <Space wrap>
        <Button disabled={!canStartGuide} type="primary" icon={<UserAddOutlined />} onClick={() => startOperationGuide(OperationGuideType.Users, history, "/users")}>开始蒙版引导</Button>
        <Link to={canStartGuide ? "/users" : "#"}>
          <Button disabled={!canStartGuide}>打开用户列表</Button>
        </Link>
      </Space>
    </section>

    <section className="usage-guide-section usage-guide-danger-section">
      <SectionTitle icon={<UserDeleteOutlined />} title="删除用户" />
      <GuideSteps items={[
        {
          title: "找到目标用户",
          description: "在用户列表中通过用户名、显示名称或其他列的筛选功能定位账号，并再次核对所属组织。",
        },
        {
          title: "执行删除",
          description: "点击该行右侧的“删除”，在确认框中核对用户名后确认。当前登录账号和内置管理员账号不能删除。",
        },
        {
          title: "确认业务影响",
          description: "删除会使用户下线并移除其用户组关系。是否保留软删除记录取决于所属组织的配置，请在操作前确认不再需要该账号。",
        },
      ]} />
    </section>
  </div>
);

const ExpirationGuide = ({history, canStartGuide}) => (
  <div className="usage-guide-tab">
    <section className="usage-guide-section">
      <SectionTitle icon={<ClockCircleOutlined />} title="设置用户有效期" />
      <Alert
        showIcon
        type="info"
        message="到达过期时间后，用户将无法继续登录；清空过期时间表示不限制有效期。"
      />
      <GuideSteps items={[
        {
          title: "打开用户编辑页",
          description: "进入“用户管理 > 用户”，点击目标用户右侧的“编辑”。",
        },
        {
          title: "选择过期时间",
          description: "找到“过期时间”，选择日期和具体时间后确认。请留意浏览器当前时区，避免实际失效时间与预期不一致。",
        },
        {
          title: "保存设置",
          description: "点击“保存”继续停留在当前页，或点击“保存 & 退出”返回用户列表。清空该字段并保存可取消有效期限制。",
        },
      ]} />
      <Button disabled={!canStartGuide} type="primary" icon={<ClockCircleOutlined />} onClick={() => startOperationGuide(OperationGuideType.Expiration, history, "/users")}>开始蒙版引导</Button>
    </section>

    <section className="usage-guide-section usage-guide-muted-section">
      <Title level={4}>看不到“过期时间”字段？</Title>
      <Paragraph>
        打开“用户管理 &gt; 组织”，编辑用户所属组织，在“个人页设置项”中添加“过期时间”，设置为可见，并将修改规则设为
        <Tag color="blue">Admin</Tag>
        后保存组织。然后重新进入用户编辑页。
      </Paragraph>
    </section>
  </div>
);

const GameGuide = ({history, canStartGuide}) => (
  <div className="usage-guide-tab">
    <section className="usage-guide-section">
      <SectionTitle icon={<AppstoreAddOutlined />} title="添加和配置游戏" />
      <GuideSteps items={[
        {
          title: "进入游戏列表",
          description: "打开“游戏管理 > 游戏”。管理多个组织时，先在页头选择目标组织，以免把游戏添加到错误的组织。",
        },
        {
          title: "添加游戏",
          description: "点击列表上方的“添加”，进入“新游戏”编辑页。填写内部名称和展示名称，并上传游戏图片。",
        },
        {
          title: "配置展示资源",
          description: "按投放需要上传 Banner 背景图、Trending 图标和视频，也可以直接填写可公开访问的资源 URL。上传后先检查页面中的预览。",
        },
        {
          title: "配置分类与排序",
          description: "在“推荐标签”多选框中选择推荐分类，不要使用普通“标签”字段代替。然后设置游戏类型、语言和游戏模式；推荐指数大于 0 时启用对应推荐。",
        },
        {
          title: "配置启动地址并发布",
          description: "填写游戏 URL；如有离线包，再填写离线包 URL。将状态设为 Published，点击“保存 & 退出”，并在列表中复核配置。",
        },
      ]} />
      <Alert
        showIcon
        type="warning"
        message="Banner 推荐必须同时配置视频 URL；视频 URL 为空时，保存会自动把 Banner 推荐指数重置为 0。"
        className="usage-guide-note"
      />
      <Space wrap>
        <Button disabled={!canStartGuide} type="primary" icon={<AppstoreAddOutlined />} onClick={() => startOperationGuide(OperationGuideType.Games, history, "/games")}>开始蒙版引导</Button>
        <Link to={canStartGuide ? "/games" : "#"}>
          <Button disabled={!canStartGuide}>打开游戏列表</Button>
        </Link>
        <Link to={canStartGuide ? "/game-tags" : "#"}>
          <Button disabled={!canStartGuide} icon={<ArrowRightOutlined />}>管理推荐标签</Button>
        </Link>
      </Space>
    </section>
  </div>
);

const ProductGuide = ({history, canStartGuide}) => (
  <div className="usage-guide-tab">
    <section className="usage-guide-section">
      <SectionTitle icon={<ShoppingCartOutlined />} title="添加和配置产品" />
      <Alert
        showIcon
        type="warning"
        message="重点：“显示名称”（产品名称）必须填写正整数"
        description="该数值用于模拟购买的金币数量。例如填写 1000，表示购买成功后模拟获得 1000 金币。内部“名称”字段保持 product_xxx 一类唯一标识即可。"
      />
      <GuideSteps items={[
        {
          title: "进入产品列表",
          description: "打开“业务与支付 > 产品”。如果管理多个组织，请先在页头选择目标组织。",
        },
        {
          title: "添加产品",
          description: "点击列表上方的“添加”进入新产品编辑页。确认内部名称不会与已有产品重复。",
        },
        {
          title: "设置模拟金币数量",
          description: "在“显示名称”中只填写大于 0 的整数，例如 100、1000 或 10000。不要填写小数、0、负数或带“金币”等文字。",
        },
        {
          title: "配置价格与渠道",
          description: "选择币种、填写支付价格和库存，付款方式选择 provider_dummy。价格代表支付金额，与显示名称中的金币数量不同。",
        },
        {
          title: "发布并测试",
          description: "将状态设为 Published，点击“保存 & 退出”。回到产品列表后点击“购买”，完成一次模拟金币购买测试。",
        },
      ]} />
      <Space wrap>
        <Button disabled={!canStartGuide} type="primary" icon={<ShoppingCartOutlined />} onClick={() => startOperationGuide(OperationGuideType.Products, history, "/products")}>开始蒙版引导</Button>
        <Link to={canStartGuide ? "/products" : "#"}>
          <Button disabled={!canStartGuide}>打开产品列表</Button>
        </Link>
      </Space>
    </section>
  </div>
);

const UsageGuidePage = ({account}) => {
  const history = useHistory();
  const canStartGuide = account !== null && account !== undefined;
  const items = [
    {key: "users", label: "用户管理", children: <UserGuide history={history} canStartGuide={canStartGuide} />},
    {key: "expiration", label: "用户有效期", children: <ExpirationGuide history={history} canStartGuide={canStartGuide} />},
    {key: "games", label: "游戏列表", children: <GameGuide history={history} canStartGuide={canStartGuide} />},
    {key: "products", label: "产品配置", children: <ProductGuide history={history} canStartGuide={canStartGuide} />},
  ];

  return (
    <main className="usage-guide">
      <div className="usage-guide-heading">
        <div>
          <Title level={2}>使用说明</Title>
          <Paragraph type="secondary">用户账号与游戏列表的日常管理操作。</Paragraph>
        </div>
        <Space wrap>
          <Link to={canStartGuide ? "/users" : "#"}><Button disabled={!canStartGuide}>用户列表</Button></Link>
          <Link to={canStartGuide ? "/games" : "#"}><Button disabled={!canStartGuide}>游戏列表</Button></Link>
          <Link to={canStartGuide ? "/products" : "#"}><Button disabled={!canStartGuide}>产品列表</Button></Link>
        </Space>
      </div>
      <Alert
        showIcon
        type="info"
        message="开始操作前，请确认页头当前选中的组织。添加、删除和编辑操作都会作用于该组织的数据。"
        className="usage-guide-org-alert"
      />
      {!canStartGuide && (
        <Alert
          showIcon
          type="warning"
          message="当前为说明预览模式"
          description="要体验页面蒙版引导，请同时启动 Casdoor 后端（默认端口 8000），并使用管理员账号登录。"
          className="usage-guide-org-alert"
        />
      )}
      <Tabs defaultActiveKey="users" items={items} />
    </main>
  );
};

export default UsageGuidePage;
